/**
 * report/variants.ts — welke keuzetekst bij welke paragraaf, en waarom (featureplan §5).
 *
 * De varianten van een paragraaf staan op volgorde. De eerste waarvan de regel past is de
 * keuze (OF); daarbij komen de varianten uit zijn `combinable` die zelf ook passen (EN/OF), en
 * daarvan weer de hunne. Zo werkt '1.2.2 Asbest' (aangetroffen EN/OF geen belemmering voor
 * hergebruik) en '3.3.1' (een strategie per gekozen onderzoek) met dezelfde regel.
 */

import type { ReportTextDef } from './defaultTexts.js';
import { evaluateRule, interpolate, showFactValue, type Facts, type RuleCheck, type Rule } from './rules.js';
import type { ReportFactValue } from './types.js';

/** Leesbare naam per feit, voor 'Gekozen omdat'. */
export const FACT_LABELS: Record<string, string> = {
  'investigation.soil': 'onderzoek bodem (NEN 5740)',
  'investigation.groundwater': 'onderzoek grondwater',
  'investigation.pfas': 'onderzoek PFAS',
  'investigation.asbestosSoil': 'onderzoek asbest in bodem (NEN 5707)',
  'investigation.asbestosRubble': 'onderzoek asbest in puinfundering (NEN 5897)',
  'investigation.asphalt': 'asfaltonderzoek (CROW 210)',
  'investigation.crow400': 'CROW 400',
  'asbestos.found': 'asbest aangetroffen',
  'asbestos.resultsKnown': 'labresultaten asbest binnen',
  'asbestos.maxValueMgKg': 'hoogste gewogen asbestgehalte (mg/kg d.s.)',
  'asbestosSoil.found': 'asbest aangetroffen in bodem',
  'asbestosRubble.found': 'asbest aangetroffen in puinfundering',
  'fieldwork.deviation': 'afwijking van normen bij het veldwerk',
  'crow400.class': 'voorlopige CROW 400-klasse',
};

export interface VariantOption {
  key: string;
  label: string;
  match: boolean;
  /** 'asbest aangetroffen in bodem: ja (ASB01, 15 mg/kg d.s.)' — waarom wel of niet. */
  reason: string;
  text: string;
}

export interface VariantChoice {
  chosen: string[];
  options: VariantOption[];
  /** De tekst van de gekozen varianten, met de feiten ingevuld. */
  text: string;
}

/** Eén zin uit de toetsen: 'asbest aangetroffen in bodem: ja (ASB01, 15 mg/kg d.s.)'. */
export function describeChecks(checks: RuleCheck[], facts: Facts): string {
  if (!checks.length) return 'Altijd van toepassing';
  return checks.map((c) => {
    const detail = facts[`${c.fact}.detail`] ?? facts[`${c.fact.replace(/\.\w+$/, '')}.detail`];
    const label = FACT_LABELS[c.fact] || c.fact;
    const extra = c.ok && detail ? ` (${showFactValue(detail)})` : '';
    return c.ok ? `${label}: ${showFactValue(c.value)}${extra}` : `${label} is ${showFactValue(c.value)}, nodig ${c.expected}`;
  }).join('; ');
}

const renderText = (keys: string[], byKey: Map<string, ReportTextDef>, facts: Facts) =>
  keys.map((k) => byKey.get(k)).filter(Boolean).map((t) => interpolate(t!.body, facts)).join('\n');

/** Kies de varianten van één paragraaf. `forced` = een keuze van de adviseur die voorgaat. */
export function chooseVariants(section: string, texts: ReportTextDef[], facts: Facts, forced: string[] | null = null): VariantChoice {
  const variants = texts.filter((t) => t.section === section && t.kind === 'variant');
  const byKey = new Map(variants.map((v) => [v.key, v]));
  const results = new Map(variants.map((v) => [v.key, evaluateRule(v.rule, facts)]));
  const options: VariantOption[] = variants.map((v) => {
    const r = results.get(v.key)!;
    return { key: v.key, label: v.label, match: r.match, reason: describeChecks(r.checks, facts), text: interpolate(v.body, facts) };
  });

  let chosen: string[];
  if (forced) {
    chosen = forced.filter((k) => byKey.has(k));
  } else {
    chosen = [];
    const primary = variants.find((v) => results.get(v.key)!.match);
    if (primary) {
      const queue = [primary.key];
      while (queue.length) {
        const k = queue.shift()!;
        if (chosen.includes(k)) continue;
        chosen.push(k);
        for (const c of byKey.get(k)?.combinable || []) if (results.get(c)?.match) queue.push(c);
      }
    }
  }
  return { chosen, options, text: renderText(chosen, byKey, facts) };
}

/** De vaste teksten van één paragraaf, met de feiten ingevuld. */
export function fixedText(section: string, texts: ReportTextDef[], facts: Facts): string {
  return texts.filter((t) => t.section === section && t.kind === 'fixed').map((t) => interpolate(t.body, facts)).join('\n');
}

/**
 * Feiten die je in een bureautekst kunt gebruiken als {{feit}}, met een omschrijving en een
 * voorbeeldwaarde. Voor de invoegknoppen en het voorbeeld in Instellingen › Rapportage.
 */
export const REPORT_TEXT_FACTS: Record<string, { label: string; example: string }> = {
  'project.name': { label: 'Projectnaam', example: 'Voorbeeldlaan 12, Dorpstad' },
  'project.code': { label: 'Projectnummer', example: 'P2026-001' },
  'borings.count': { label: 'Aantal boringen', example: '38' },
  'sublocations.count': { label: 'Aantal deellocaties', example: '2' },
  'groundwater.peilbuizen': { label: 'Aantal peilbuizen', example: '9' },
  'asbestos.depthM': { label: 'Diepte asbestonderzoek (m)', example: '2,0' },
  'asbestos.maxValueMgKg': { label: 'Hoogste gewogen asbestgehalte (mg/kg d.s.)', example: '12' },
  'crow400.class': { label: 'Voorlopige CROW 400-klasse', example: 'basishygiëne' },
};

/** De feiten die in een tekst staan ({{…}}), in volgorde en zonder dubbele. */
export function factsInText(text: string): string[] {
  return [...new Set([...String(text || '').matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]))];
}

const SAY: Record<string, (v: unknown) => string> = {
  eq: (v) => (v === true ? '' : v === false ? 'niet' : `= ${showFactValue(v as ReportFactValue)}`),
  ne: (v) => `≠ ${showFactValue(v as ReportFactValue)}`,
  gt: (v) => `boven ${v}`, gte: (v) => `vanaf ${v}`, lt: (v) => `onder ${v}`, lte: (v) => `tot en met ${v}`,
  in: (v) => `een van ${(v as unknown[]).join(', ')}`, exists: (v) => (v ? 'bekend' : 'onbekend'),
};

/**
 * Een regel als zin, voor 'Gebruikt als …' in de instellingen: 'onderzoek asbest in bodem
 * (NEN 5707) en niet asbest aangetroffen'. Geen regel: altijd.
 */
export function describeRule(rule: Rule | null | undefined): string {
  if (!rule) return 'altijd van toepassing';
  const r = rule as any;
  if (r.all) return r.all.map((c: any) => (c.any?.length > 1 ? `(${describeRule(c)})` : describeRule(c))).join(' en ');
  if (r.any) return r.any.map(describeRule).join(' of ');
  if (r.not) return `niet (${describeRule(r.not)})`;
  const label = FACT_LABELS[r.fact] || r.fact;
  const op = Object.keys(SAY).find((k) => k in r);
  if (!op) return label;
  const said = SAY[op](r[op]);
  if (said === '') return label;
  if (said === 'niet') return `niet ${label}`;
  return `${label} ${said}`;
}
